import { useState } from "react";
import { useLocation } from "wouter";
import { Footer } from "../components/Footer";
import { Navbar } from "../components/Navbar";
import { SearchModal } from "../components/SearchModal";
import { useForecast } from "../lib/api";
import { useUnit } from "../lib/hooks";
import { REGIONS } from "../lib/regions";
import { useScrollToTopOnMount } from "../lib/scrollRestoration";
import { STATIC_SPOTS } from "../lib/staticSpots";
import { useMeta } from "../lib/useMeta";
import { knotsColor } from "../lib/utils";
import styles from "./HowItWorksPage.module.css";

/**
 * /how-it-works — explainer page covering what the site is, why it
 * exists, and how to read the forecast. Reachable from the footer.
 *
 * Voice follows docs/content-guidelines.md: direct, confident, specific
 * over generic, no salesy energy. Inline legends for the wind-direction
 * arrow colors and the knot-bar colors so users can decode the
 * overview and detail pages without having to ask.
 */
export function HowItWorksPage() {
  const [location, setLocation] = useLocation();
  useScrollToTopOnMount(location);
  const { data } = useForecast();
  const { unit, cycleUnit } = useUnit();
  const [searchOpen, setSearchOpen] = useState(false);

  const searchSpots = data?.spots ?? STATIC_SPOTS;

  useMeta({
    title: "How it works — Big Wind",
    description:
      "How Big Wind merges multiple forecast providers into one read on each spot, so you stop juggling apps before a session.",
    path: "/how-it-works",
  });

  return (
    <div className={styles.layout}>
      <div className={styles.navbarSticky}>
        <div className={styles.contentColumn}>
          <Navbar
            variant="region"
            unit={unit}
            onCycleUnit={cycleUnit}
            onOpenSearch={() => setSearchOpen(true)}
            onBack={() => setLocation("/")}
          />
        </div>
      </div>

      <article className={styles.contentColumn}>
        <header className={styles.pageHeader}>
          <p className={styles.eyebrow}>How it works</p>
          <h1 className={styles.title}>
            A wind forecast that knows your spot
          </h1>
        </header>

        {/* About */}
        <Section heading="What this is">
          <p>
            Big Wind is a wind forecast for windboard sport spots across
            Sweden. Built for people who wingfoil, kite, windsurf, or otherwise
            need to know if today's wind is worth driving for.
          </p>
          <p>
            Your other apps tell you the regional weather. This one tells
            you whether it's worth heading out, where, and when.
          </p>
        </Section>

        {/* Coverage / regions */}
        <Section heading="Where we cover">
          <p>
            All of Sweden, split into eight geographic regions. Each region
            covers one or more of Sweden's 21 official län.
          </p>
          <ul className={styles.regionList}>
            {REGIONS.map((r) => (
              <li key={r.id}>
                <strong>{r.label}</strong>
                {r.lan.length > 0 && (
                  <span className={styles.regionLan}>
                    {" "}
                    ({r.lan.join(", ")})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>

        {/* How to read the forecast */}
        <Section heading="Reading the forecast">
          <p>
            Each spot has an <strong>ideal wind direction</strong> set,
            usually 2-6 of the 16 compass directions that the spot is set
            up to work in.
          </p>
          <p>
            We split the visuals so each colour means just one thing.
            Wind <strong>arrows</strong> tell you about
            <strong> direction</strong> (how close the wind is to a useful
            angle for this spot). The <strong>knot bars and strips</strong>
            tell you about <strong>speed</strong>. You decide whether the
            speed is enough for your gear and skill. We don't presume.
          </p>

          <Legend
            title="Arrow colours: direction match only"
            items={[
              {
                color: "var(--wind-direction-prime-default)",
                label: "Ideal",
                desc: "Wind blows from one of the spot's ideal directions.",
              },
              {
                color: "var(--wind-direction-good-default)",
                label: "Close enough",
                desc: "One compass cell out. Within forecast tolerance, since real wind often lands inside the ideal range anyway.",
              },
              {
                color: "var(--wind-direction-neutral-default)",
                label: "Off-axis",
                desc: "Mid-quadrant. Wrong angle for this spot, but sailable in principle.",
              },
              {
                color: "var(--wind-direction-unlikely-default)",
                label: "Offshore",
                desc: "Blowing from land out to the water, opposite of the spot's working directions. Won't deliver wind at the launch.",
              },
            ]}
          />

          <p>
            On the overview map and the "today" column in spot lists, the
            arrow shows the <strong>current hour's</strong> wind rather
            than the whole-day average. Once the morning has passed, the
            average is partly historical. What blew at 9am isn't what
            you'll feel when you get to the beach.
          </p>

          <p>
            Each <strong>spot marker</strong> on the map carries both
            signals at once: the inner coloured disc reads as the
            direction match (same scale as the arrows above), and a
            colored arc orbits it whose length encodes wind speed and
            whose colour follows the same scale as the chart bars below.
            A full bright-green ring around a green disc means
            <em> direction and speed both land in your zone</em>; a
            faint sliver around a muted disc means dead calm.
          </p>

          <p>
            The hourly chart on each spot's detail page colours the
            <strong> bars by knot range</strong>, so the speed curve is
            independent from the arrow's direction read. Combine the two
            yourself. Your gear and skill know better than any single score.
          </p>

          <KnotsLegend />

          <p>
            The scale's bright-green plateau between 15–25 kn is the
            rideable sweet spot for most wing / kite / windsurf gear.
            It stays green across that range so you read "full power"
            consistently, then warms through lime to red as wind
            crosses into overpowered.
          </p>
        </Section>

        {/* Filtering the overview */}
        <Section heading="Filtering the overview">
          <p>
            The region overview has a sort dropdown just below the day
            strip. Four options, all driven by the spot-aware scorer
            (combined direction + speed match, tie-broken by raw wind
            speed so a spot with stronger conditions edges out a weaker
            one when their scores land on the same bucket):
          </p>
          <ul className={styles.regionList}>
            <li>
              <strong>Best wind today</strong> — ranks spots by their
              combined score for the day the strip is on. Tap a different
              day to leaf through the week; the dropdown label follows
              ("Best wind tomorrow", "Best wind Friday 22nd").
            </li>
            <li>
              <strong>Best wind this week</strong> — finds the day where
              some spot peaks across the whole forecast, pins that day in
              the strip, and lists the top 3 spots for that day under a
              "Best [Day]" header. Everything else lands in "Other spots"
              ranked the same way.
            </li>
            <li>
              <strong>By area</strong> — groups spots alphabetically by
              area within the region.
            </li>
            <li>
              <strong>Spots A–Z</strong> — straight alphabetical.
            </li>
          </ul>
        </Section>

        {/* Closing */}
        <Section heading="Built by riders, free, no sign-up">
          <p>
            One person built this because they got tired of checking multiple
            apps to figure out a session. It's free, has no ads, and won't
            ask for your email.
          </p>
          <p>
            Spot info is a mix of community-sourced data and hand curation.
            If something's wrong (a coord, a direction range, a missing
            spot) Let us know{" "}
            <a
              href="mailto:info@bigwind.surf"
              className={styles.inlineLink}
            >
              info@bigwind.surf
            </a>
            .
          </p>
        </Section>
      </article>

      <div className={styles.contentColumn}>
        <Footer />
      </div>

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        spots={searchSpots}
        unit={unit}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Section({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{heading}</h2>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

interface LegendItem {
  color: string;
  label: string;
  desc: string;
}

function Legend({ title, items }: { title: string; items: LegendItem[] }) {
  return (
    <div className={styles.legend}>
      <p className={styles.legendTitle}>{title}</p>
      <ul className={styles.legendList}>
        {items.map((item) => (
          <li key={item.label} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ background: item.color }}
              aria-hidden
            />
            <span>
              <strong>{item.label}:</strong> {item.desc}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Visualises the knot color scale with the same RGB stops the chart
 *  uses, so the legend never drifts from the chart it explains. */
function KnotsLegend() {
  // Stops match the bands in CHART_KNOT_STOPS (utils.ts): cool ramp up
  // to the 15–25 kn green plateau, then a quick warm-up through lime →
  // orange → red as wind crosses into overpowered.
  const stops = [
    { kn: 1, label: "0–3" },
    { kn: 6, label: "4–9" },
    { kn: 12, label: "10–14" },
    { kn: 20, label: "15–25" },
    { kn: 30, label: "26–32" },
    { kn: 40, label: "33–45" },
    { kn: 55, label: "46+" },
  ];
  return (
    <div className={styles.legend}>
      <p className={styles.legendTitle}>Knots scale (chart bar colors)</p>
      <div className={styles.knotsGrid}>
        {stops.map((s) => (
          <div key={s.kn} className={styles.knotCell}>
            <span
              className={styles.knotSwatch}
              style={{ background: knotsColor(s.kn) }}
              aria-hidden
            />
            <span className={styles.knotLabel}>{s.label} kn</span>
          </div>
        ))}
      </div>
      <p className={styles.legendCaption}>
        Bar colors always come from knots regardless of your display unit.
        Switching units only changes the numbers.
      </p>
    </div>
  );
}
